import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Star } from "lucide-react";

interface CMSPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: any;
  pageKey: string;
}

export const CMSPreview = ({ open, onOpenChange, content, pageKey }: CMSPreviewProps) => {
  const renderPreviewContent = () => {
    if (!content) return <p className="text-muted-foreground">Aucun contenu à prévisualiser</p>;

    return (
      <div className="space-y-8">
        {/* Hero Section */}
        {content.hero && (
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-lg">
            {content.hero.badge && (
              <Badge variant="secondary" className="mb-4">
                {content.hero.badge}
              </Badge>
            )}
            <h1 className="text-4xl font-bold mb-4">
              {content.hero.title}{' '}
              {content.hero.titleHighlight && (
                <span className="text-primary">{content.hero.titleHighlight}</span>
              )}
            </h1>
            {content.hero.description && (
              <div 
                className="text-lg text-muted-foreground mb-6"
                dangerouslySetInnerHTML={{ __html: content.hero.description }}
              />
            )}
            <div className="flex gap-4">
              {content.hero.ctaMain && (
                <Button size="lg">{content.hero.ctaMain}</Button>
              )}
              {content.hero.ctaSecondary && (
                <Button variant="outline" size="lg">{content.hero.ctaSecondary}</Button>
              )}
            </div>
          </div>
        )}

        {/* Stats Section */}
        {content.stats && content.stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {content.stats.map((stat: any, index: number) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Benefits Section */}
        {content.benefits && content.benefits.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Avantages</h2>
            <div className="grid gap-4">
              {content.benefits.map((benefit: any, index: number) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        {benefit.title}
                        {benefit.highlight && (
                          <Badge variant="secondary" className="ml-2">
                            {benefit.highlight}
                          </Badge>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: benefit.description }}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Sections */}
        {content.sections && content.sections.length > 0 && (
          <div className="space-y-6">
            {content.sections.map((section: any, index: number) => (
              <div key={index}>
                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                <div 
                  className="prose prose-sm max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </div>
            ))}
          </div>
        )}

        {/* FAQ Section */}
        {content.faq && content.faq.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Questions fréquentes</h2>
            <div className="space-y-4">
              {content.faq.map((item: any, index: number) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials */}
        {content.testimonials && content.testimonials.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Témoignages</h2>
            <div className="grid gap-4">
              {content.testimonials.map((testimonial: any, index: number) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                      ))}
                    </div>
                    <div 
                      className="text-muted-foreground mb-4"
                      dangerouslySetInnerHTML={{ __html: testimonial.content }}
                    />
                    <div className="font-semibold">{testimonial.author}</div>
                    {testimonial.role && (
                      <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Prévisualisation de la page</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {renderPreviewContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
